import React, { useState, useEffect } from 'react';

const ImageGallery = ({ images, productName }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [imageLoadStates, setImageLoadStates] = useState({});
  const [retryAttempts, setRetryAttempts] = useState({});
  
  const MAX_RETRIES = 2;

  // Initialize image load states
  useEffect(() => {
    if (images && images.length > 0) {
      const initialStates = {};
      const initialRetries = {};
      images.forEach((_, index) => {
        initialStates[index] = 'loading';
        initialRetries[index] = 0;
      });
      setImageLoadStates(initialStates);
      setRetryAttempts(initialRetries);
    }
  }, [images]);

  if (!images || images.length === 0) {
    return (
      <div className="w-full h-96 bg-gray-200 flex items-center justify-center rounded-lg">
        <span className="text-gray-500">No images available</span>
      </div>
    );
  }

  const handleImageLoad = (index) => {
    setImageLoadStates(prev => ({ ...prev, [index]: 'loaded' }));
  };

  const handleImageError = (index, imgElement) => {
    const currentUrl = imgElement.src;
    const retryCount = retryAttempts[index] || 0;
    
    // Check if it's a problematic external domain
    const isExternalDomain = currentUrl.includes('selltech.com.bd') || 
                            currentUrl.includes('startech.com.bd') ||
                            currentUrl.includes('daraz.com') ||
                            currentUrl.includes('alibaba.com');
    
    if (retryCount < MAX_RETRIES && !isExternalDomain) {
      // Try adding cache-busting parameters for non-external domains
      const separator = currentUrl.includes('?') ? '&' : '?';
      const newUrl = `${currentUrl}${separator}t=${Date.now()}&retry=${retryCount + 1}`;
      
      setRetryAttempts(prev => ({
        ...prev,
        [index]: retryCount + 1
      }));
      
      // Retry with new URL
      setTimeout(() => {
        imgElement.src = newUrl;
      }, 500 * (retryCount + 1)); // Exponential backoff
    } else {
      // For external domains or after max retries, use fallback immediately
      console.warn(`Image failed to load (external domain or max retries):`, currentUrl);
      
      // Use different fallback images based on product category or index
      const fallbackImages = [
        'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=400&h=300&fit=crop', // Generic product
        'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=400&h=300&fit=crop', // Watch/tech
        'https://images.unsplash.com/photo-1572569511254-d8f925fe2cbb?w=400&h=300&fit=crop', // Electronics
        'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=400&h=300&fit=crop'  // Gadgets
      ];
      
      imgElement.src = fallbackImages[index % fallbackImages.length];
      setImageLoadStates(prev => ({
        ...prev,
        [index]: 'error'
      }));
    }
  };

  const handleImageChange = (index) => {
    if (index === currentImageIndex) return;
    
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentImageIndex(index);
      setIsTransitioning(false);
    }, 150);
  };

  const nextImage = () => {
    const nextIndex = (currentImageIndex + 1) % images.length;
    handleImageChange(nextIndex);
  };

  const prevImage = () => {
    const prevIndex = currentImageIndex === 0 ? images.length - 1 : currentImageIndex - 1;
    handleImageChange(prevIndex);
  };

  return (
    <div className="space-y-4">
      {/* Main Image Display */}
      <div className="relative overflow-hidden rounded-lg bg-gray-100">
        <div className="aspect-square relative">
          {imageLoadStates[currentImageIndex] === 'loading' && (
            <div className="absolute inset-0 bg-gray-200 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          )}
          
          <img
            src={images[currentImageIndex]}
            alt={`Product view ${currentImageIndex + 1}`}
            className={`w-full h-full object-cover transition-all duration-300 ${
              isTransitioning ? 'opacity-0 scale-95' : 'opacity-100 scale-100'
            } ${imageLoadStates[currentImageIndex] === 'loading' ? 'opacity-0' : 'opacity-100'}`}
            onLoad={() => handleImageLoad(currentImageIndex)}
            onError={(e) => handleImageError(currentImageIndex, e.target)}
            crossOrigin="anonymous"
            referrerPolicy="no-referrer"
          />
          
          {/* Navigation Arrows */}
          {images.length > 1 && (
            <>
              <button
                onClick={prevImage}
                className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75 transition-all duration-200"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={nextImage}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75 transition-all duration-200"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}
          
          {/* Image Counter */}
          {images.length > 1 && (
            <div className="absolute bottom-2 right-2 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-sm">
              {currentImageIndex + 1} / {images.length}
            </div>
          )}
        </div>
      </div>

      {/* Thumbnail Navigation */}
      {images.length > 1 && (
        <div className="flex space-x-2 overflow-x-auto pb-2">
          {images.map((image, index) => (
            <button
              key={index}
              onClick={() => handleImageChange(index)}
              className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                index === currentImageIndex
                  ? 'border-blue-500 ring-2 ring-blue-200'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="relative w-full h-full">
                {imageLoadStates[index] === 'loading' && (
                  <div className="absolute inset-0 bg-gray-200 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></div>
                  </div>
                )}
                <img
                  src={image}
                  alt={`${productName} thumbnail ${index + 1}`}
                  className={`w-full h-full object-cover transition-opacity duration-200 ${
                    imageLoadStates[index] === 'loading' ? 'opacity-0' : 'opacity-100'
                  }`}
                  onLoad={() => handleImageLoad(index)}
                  onError={(e) => handleImageError(index, e.target)}
                  crossOrigin="anonymous"
                  referrerPolicy="no-referrer"
                />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ImageGallery;
